// src/Container/VerifyEmail/verifyEmail.js

import React, { useEffect, useState, useRef } from 'react';
import { auth, functions, db } from '../../Firebase/firebase';
import {
  onAuthStateChanged,
  reload,
  signInWithEmailAndPassword,
  setPersistence,
  browserSessionPersistence,
  updateProfile,
  signOut,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { doc, writeBatch, collection, query, where, getDocs } from 'firebase/firestore';

import {
  Box,
  Typography,
  Button,
  CircularProgress,
  TextField,
  Dialog,
  DialogContent,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import ConfirmDialog from '../../Components/ConfirmDialog/ConfirmDialog';

export default function VerifyEmail({ open, onClose, linkingData }) {
  const [dialogState, setDialogState] = useState({
    open: false,
    severity: 'info',
    message: '',
    autoClose: 0,
    onConfirm: null,
  });

  const [cooldown, setCooldown] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isResending, setIsResending] = useState(false);

  // State for the 6-digit OTP
  const [otp, setOtp] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  const timerRef = useRef(null);

  useEffect(() => {
    const timeoutId = timerRef.current;
    return () => timeoutId && clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    if (!open) {
      setOtp('');
      setCooldown(0);
      setIsVerifying(false);
      setIsDeleting(false);
      setIsResending(false);
    }
  }, [open]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (open && !user && !linkingData) {
        onClose();
      }
    });
    return unsub;
  }, [open, linkingData, onClose]);

  const addToCollections = async (user, displayName, companyName = '') => {
    const batch = writeBatch(db);
    const email = user.email.toLowerCase().trim();
    const date = new Date().toISOString();

    try {
      const q = query(collection(db, 'mailingList'), where('email', '==', email));
      const snapshot = await getDocs(q);
      snapshot.forEach((docSnap) => {
        if (docSnap.id !== user.uid) {
          batch.delete(docSnap.ref);
        }
      });
    } catch (queryError) {
      console.warn('Could not query mailing list for duplicates.', queryError);
    }

    const mailingRef = doc(db, 'mailingList', user.uid);
    batch.set(
      mailingRef,
      {
        userName: displayName || '',
        companyName: companyName || '',
        email: email,
        addedAt: date,
      },
      { merge: true },
    );

    await batch.commit();
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) return;
    if (!linkingData && !auth.currentUser) return;

    setIsVerifying(true);

    try {
      if (linkingData) {
        const verifyReset = httpsCallable(functions, 'verifyPasswordResetOtp');
        await verifyReset({
          email: linkingData.email,
          otp: otp,
          newPassword: linkingData.password,
        });

        await setPersistence(auth, browserSessionPersistence);
        const userCredential = await signInWithEmailAndPassword(
          auth,
          linkingData.email,
          linkingData.password,
        );

        await updateProfile(userCredential.user, { displayName: linkingData.userName });
        await addToCollections(
          userCredential.user,
          linkingData.userName,
          linkingData.companyName,
        );

        setDialogState({
          open: true,
          severity: 'success',
          message: 'Email verified! Logging you in...',
          autoClose: 0,
          onConfirm: null,
        });

        timerRef.current = setTimeout(() => {
          setDialogState({ ...dialogState, open: false });
          onClose();
        }, 1500);
      } else {
        const verifyEmailOtp = httpsCallable(functions, 'verifyEmailOtp');
        await verifyEmailOtp({ otp });

        await reload(auth.currentUser);

        // AMENDED: Force token refresh so onAuthStateChanged detects verified status immediately
        await auth.currentUser.getIdToken(true);

        if (auth.currentUser.emailVerified) {
          setDialogState({
            open: true,
            severity: 'success',
            message: 'Email verified! Logging you in...',
            autoClose: 0,
            onConfirm: null,
          });

          timerRef.current = setTimeout(() => {
            setDialogState({ ...dialogState, open: false });
            onClose();
          }, 1500);
        }
      }
    } catch (error) {
      setDialogState({
        open: true,
        severity: 'error',
        message: error.message,
        autoClose: 3000,
        onConfirm: null,
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (!linkingData && !auth.currentUser) return;
    setIsResending(true);

    try {
      if (linkingData) {
        const sendLinkOtp = httpsCallable(functions, 'sendLinkAccountOtp');
        await sendLinkOtp({ email: linkingData.email });
      } else {
        const resendEmail = httpsCallable(functions, 'resendVerificationEmail');
        await resendEmail();
      }

      setDialogState({
        open: true,
        severity: 'success',
        message: 'Verification code sent!',
        autoClose: 2000,
        onConfirm: null,
      });

      setOtp('');
      setCooldown(30);
      const cd = setInterval(() => {
        setCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(cd);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      setDialogState({
        open: true,
        severity: 'error',
        message: error.message,
        autoClose: 2000,
        onConfirm: null,
      });
    } finally {
      setIsResending(false);
    }
  };

  const handleCancelRequest = () => {
    setDialogState({
      open: true,
      severity: 'warning',
      message: 'This will cancel the signup process. Proceed?',
      autoClose: 0,
      onConfirm: executeCancel,
    });
  };

  const executeCancel = async () => {
    setDialogState((prev) => ({ ...prev, open: false }));

    if (linkingData) {
      onClose();
      return;
    }

    if (!auth.currentUser) return;

    setIsDeleting(true);
    const userEmail = auth.currentUser.email;

    try {
      const deleteUnverified = httpsCallable(functions, 'deleteUnverifiedAccount');
      await deleteUnverified({ email: userEmail });
      await signOut(auth);
      onClose();
    } catch (error) {
      setIsDeleting(false);
      console.error('Cancel failed:', error);
      setDialogState({
        open: true,
        severity: 'error',
        message: 'Could not cancel process. Please try again.',
        autoClose: 3000,
        onConfirm: null,
      });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='xs'
      fullWidth
      sx={{ zIndex: 100000 }}
      PaperProps={{ sx: { borderRadius: 3, overflow: 'visible', position: 'relative' } }}
      BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.2)' } }}>
      <ConfirmDialog
        open={dialogState.open}
        onClose={() => setDialogState({ ...dialogState, open: false })}
        message={dialogState.message}
        severity={dialogState.severity}
        autoClose={dialogState.autoClose}
        onConfirm={dialogState.onConfirm}
        sx={{ zIndex: 100005 }}
      />

      <IconButton
        onClick={onClose}
        sx={{ position: 'absolute', top: 8, right: 8, zIndex: 100 }}>
        <CloseIcon />
      </IconButton>

      <DialogContent
        sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box sx={{ width: '100%', textAlign: 'center' }}>
          <Typography component='h1' variant='h5' fontWeight='bold' sx={{ mb: 2 }}>
            Verify Your Email
          </Typography>

          <Typography variant='body1' sx={{ mb: 2 }}>
            A 6-digit verification code has been sent to your email. Please enter the code
            below to activate your account.
          </Typography>

          <Typography
            variant='body2'
            sx={{ mb: 2, fontWeight: 'bold', color: 'text.secondary' }}>
            This code will expire in 15 minutes.
          </Typography>

          <TextField
            size='small'
            margin='dense'
            fullWidth
            label='6-Digit Code'
            variant='outlined'
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            disabled={isVerifying || isDeleting || isResending}
            sx={{ mb: 2, '& .MuiInputBase-root': { height: 48 } }}
            inputProps={{
              maxLength: 6,
              style: {
                textAlign: 'center',
                letterSpacing: '0.5em',
                fontSize: '1.25rem',
                fontWeight: 'bold',
              },
            }}
          />

          <Button
            variant='contained'
            fullWidth
            disabled={otp.length !== 6 || isVerifying || isDeleting || isResending}
            onClick={handleVerifyOtp}
            sx={{
              mb: 3,
              height: '40px',
              backgroundColor: '#2e7d32',
              '&:hover': {
                backgroundColor: '#1b5e20',
                boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
              },
              '&:active': { backgroundColor: '#2e7d32' },
              '&.Mui-disabled': { backgroundColor: '#a5d6a7', color: '#fff' },
            }}>
            {isVerifying && <CircularProgress size={20} sx={{ color: 'white', mr: 2 }} />}
            Submit Code
          </Button>

          <Typography
            variant='body2'
            sx={{ mb: 3, fontSize: '0.75rem', color: 'text.secondary' }}>
            NOTE: Your email server may take a moment to forward the verification email.
            Please be patient.
          </Typography>

          <Button
            variant='contained'
            fullWidth
            disabled={cooldown > 0 || isDeleting || isResending || isVerifying}
            onClick={handleResend}
            sx={{
              mb: 2,
              height: '36px',
              backgroundColor: '#1976d2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                backgroundColor: '#1565c0',
                boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                transform: 'scale(1.03)',
              },
              '&:active': { backgroundColor: '#1976d2' },
              '&.Mui-disabled': { backgroundColor: '#1976d2', color: '#fff' },
            }}>
            {isResending ? (
              <CircularProgress size={20} sx={{ color: 'inherit', mr: 2 }} />
            ) : null}
            {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend Verification Code'}
          </Button>

          <Button
            variant='outlined'
            color='error'
            fullWidth
            disabled={isDeleting || isResending || isVerifying}
            onClick={handleCancelRequest}
            sx={{
              height: '36px',
              borderColor: '#d32f2f',
              color: '#d32f2f',
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: '#ffebee',
                borderColor: '#c62828',
                transform: 'scale(1.03)',
              },
              '&:active': { backgroundColor: 'transparent' },
              '&.Mui-disabled': { borderColor: '#d32f2f', color: '#d32f2f' },
            }}>
            {isDeleting && (
              <CircularProgress size={20} sx={{ color: 'inherit', mr: 1 }} />
            )}
            Wrong Email? Cancel Signup
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
